import { useState } from "react";
import TOS from "./components/TOS";
import axios, { trackEvent, TOKEN_KEY } from "./utils/api";

const CARD  = "#0a1020";
const CARD2 = "#0d1728";
const CARD3 = "#13213a";
const BD    = "#1b3154";
const TX    = "#d9e7fb";
const TX2   = "#7890b2";
const GR    = "#00e676";
const BL    = "#38bdf8";
const RD    = "#ef5350";

function apiError(e) {
  return e?.response?.data?.error || e.message || "Something went wrong.";
}

function Spinner() {
  return (
    <span style={{ display: "inline-flex", gap: 4, alignItems: "center" }}>
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          style={{
            width: 5, height: 5, borderRadius: "50%", background: TX2,
            animation: `dotPulse 1.2s ${i * 0.2}s infinite ease-in-out`,
            display: "inline-block",
          }}
        />
      ))}
    </span>
  );
}

function Field({ label, type = "text", value, onChange, placeholder, onEnter }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: "block", fontSize: 8, color: TX2, letterSpacing: 2, marginBottom: 5, fontFamily: "'IBM Plex Mono', monospace" }}>
        {label}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        onKeyDown={(e) => e.key === "Enter" && onEnter?.()}
        style={{ width: "100%", background: CARD3, border: `1px solid ${BD}`, borderRadius: 8, padding: "10px 14px", color: TX, fontSize: 11, boxSizing: "border-box", fontFamily: "'IBM Plex Mono', monospace", outline: "none", transition: "border-color 0.15s" }}
        onFocus={(e) => (e.target.style.borderColor = `${GR}60`)}
        onBlur={(e) => (e.target.style.borderColor = BD)}
      />
    </div>
  );
}

export default function AuthModal({ defaultTab = "login", onSuccess, onClose }) {
  const [tab,       setTab]       = useState(defaultTab);
  const [username,  setUsername]  = useState("");
  const [email,     setEmail]     = useState("");
  const [password,  setPassword]  = useState("");
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState("");
  const [tosAgreed, setTosAgreed] = useState(false);
  const [showTOS,   setShowTOS]   = useState(false);

  const [resetMode,    setResetMode]    = useState(false);
  const [resetEmail,   setResetEmail]   = useState("");
  const [resetLoading, setResetLoading] = useState(false);
  const [resetSent,    setResetSent]    = useState(false);

  const [loginCodeMode,  setLoginCodeMode]  = useState(false);
  const [loginCodeEmail, setLoginCodeEmail] = useState("");
  const [loginCode,      setLoginCode]      = useState("");
  const [codeResent,     setCodeResent]     = useState(false);
  const [codeResending,  setCodeResending]  = useState(false);

  const reset = (newTab) => { setTab(newTab); setError(""); setResetMode(false); setResetSent(false); setLoginCodeMode(false); };

  const handleLogin = async () => {
    if (!email.trim() || !password) { setError("Please enter your email and password."); return; }
    setLoading(true); setError("");
    try {
      const res = await axios.post("/api/auth/login", { email: email.trim(), password });
      if (res.data.pendingLoginCode) {
        setLoginCodeEmail(res.data.email);
        setLoginCodeMode(true);
        return;
      }
      localStorage.setItem(TOKEN_KEY, res.data.token);
      trackEvent("login", { method: "password" });
      onSuccess(res.data.user);
    } catch (e) { setError(apiError(e)); }
    finally { setLoading(false); }
  };

  const handleVerifyLoginCode = async () => {
    if (!/^\d{6}$/.test(loginCode.trim())) { setError("Enter the 6-digit code from your email."); return; }
    setLoading(true); setError("");
    try {
      const res = await axios.post("/api/auth/verify-login-code", { email: loginCodeEmail, code: loginCode.trim() });
      localStorage.setItem(TOKEN_KEY, res.data.token);
      trackEvent("login", { method: "password+code" });
      onSuccess(res.data.user);
    } catch (e) { setError(apiError(e)); }
    finally { setLoading(false); }
  };

  const handleResendLoginCode = async () => {
    setCodeResending(true); setError(""); setCodeResent(false);
    try {
      await axios.post("/api/auth/resend-login-code", { email: loginCodeEmail });
      setCodeResent(true);
    } catch (e) { setError(apiError(e)); }
    finally { setCodeResending(false); }
  };

  const handleRegister = async () => {
    if (!username.trim() || !email.trim() || !password) { setError("Please fill in all fields."); return; }
    if (!tosAgreed) { setError("Please agree to the Terms of Service."); return; }
    setLoading(true); setError("");
    try {
      const res = await axios.post("/api/auth/register", { email: email.trim(), password, displayName: username.trim() });
      localStorage.setItem(TOKEN_KEY, res.data.token);
      trackEvent("signup", { method: "password" });
      onSuccess(res.data.user);
    } catch (e) { setError(apiError(e)); }
    finally { setLoading(false); }
  };

  const handleForgotPassword = async () => {
    if (!resetEmail.trim()) { setError("Please enter your email address."); return; }
    setResetLoading(true); setError("");
    try {
      await axios.post("/api/auth/forgot-password", { email: resetEmail.trim() });
      setResetSent(true);
    } catch (e) { setError(apiError(e)); }
    finally { setResetLoading(false); }
  };

  const submit = tab === "login" ? handleLogin : handleRegister;

  return (
    <>
      {showTOS && <TOS onClose={() => setShowTOS(false)} />}
      <div
        className="ts-scan-shell"
        style={{ position: "fixed", inset: 0, background: "rgba(5,8,20,0.88)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2000, backdropFilter: "blur(10px)", padding: 16 }}
        onClick={onClose}
      >
        <style>{`
          @keyframes modalIn{from{opacity:0;transform:scale(0.95) translateY(12px)}to{opacity:1;transform:scale(1) translateY(0)}}
          @keyframes dotPulse{0%,80%,100%{transform:scale(0.6);opacity:0.4}40%{transform:scale(1);opacity:1}}
        `}</style>

        <div
          className="ts-panel"
          style={{ background: CARD, border: `1px solid ${BD}`, borderRadius: 14, padding: "32px 32px 28px", width: 390, maxWidth: "94vw", maxHeight: "92vh", overflowY: "auto", animation: "modalIn 0.2s ease-out", boxShadow: "0 32px 80px rgba(0,0,0,0.62)", position: "relative" }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Logo */}
          <div style={{ textAlign: "center", marginBottom: 24 }}>
            <div className="ts-gradient-text" style={{ fontFamily: "'Space Grotesk',sans-serif", fontWeight: 900, fontSize: 21, letterSpacing: 3 }}>
              Trade Scanner
            </div>
            <div className="ts-loading-dots" style={{ justifyContent: "center", marginTop: 10 }}><i /><i /><i /></div>
            <div style={{ fontSize: 9, color: TX2, marginTop: 5, letterSpacing: 1 }}>
              {loginCodeMode ? "Check your email" : resetMode ? "Password reset" : tab === "login" ? "Welcome back" : "Create your account"}
            </div>
          </div>

          {loginCodeMode ? (
            <>
              <div style={{ background: `${BL}12`, border: `1px solid ${BL}40`, borderRadius: 10, padding: "13px 15px", fontSize: 10, color: TX2, marginBottom: 16, lineHeight: 1.6 }}>
                We sent a 6-digit code to <span style={{ color: TX }}>{loginCodeEmail}</span>. Enter it below to sign in.
              </div>
              <Field label="SIGN-IN CODE" value={loginCode} onChange={(v) => setLoginCode(v.replace(/\D/g, "").slice(0, 6))} placeholder="123456" onEnter={handleVerifyLoginCode} />
              {codeResent && (
                <div style={{ background: `${GR}12`, border: `1px solid ${GR}40`, borderRadius: 8, padding: "9px 13px", fontSize: 10, color: GR, marginBottom: 16 }}>
                  A new code is on its way.
                </div>
              )}
              {error && (
                <div style={{ background: `${RD}12`, border: `1px solid ${RD}40`, borderRadius: 8, padding: "9px 13px", fontSize: 10, color: RD, marginBottom: 16, lineHeight: 1.5 }}>
                  {error}
                </div>
              )}
              <button
                onClick={handleVerifyLoginCode}
                disabled={loading}
                style={{ width: "100%", background: loading ? CARD2 : `linear-gradient(135deg,${GR},${BL})`, border: "none", borderRadius: 10, padding: "13px 0", color: loading ? TX2 : "#000", fontSize: 11, fontWeight: 800, cursor: loading ? "not-allowed" : "pointer", letterSpacing: 2, fontFamily: "'IBM Plex Mono',monospace", marginBottom: 12, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}
              >
                {loading ? <Spinner /> : "VERIFY →"}
              </button>
              <div style={{ textAlign: "center", fontSize: 9, color: TX2, display: "flex", justifyContent: "center", gap: 14 }}>
                <span onClick={codeResending ? undefined : handleResendLoginCode} style={{ color: BL, cursor: codeResending ? "default" : "pointer", textDecoration: "underline" }}>
                  {codeResending ? "Sending…" : "Resend code"}
                </span>
                <span onClick={() => { setLoginCodeMode(false); setLoginCode(""); setError(""); setCodeResent(false); }} style={{ color: TX2, cursor: "pointer", textDecoration: "underline" }}>
                  ← Back
                </span>
              </div>
            </>
          ) : resetMode ? (
            <>
              {resetSent ? (
                <div style={{ background: `${GR}12`, border: `1px solid ${GR}40`, borderRadius: 10, padding: "16px", fontSize: 10, color: GR, textAlign: "center", lineHeight: 1.7, marginBottom: 16 }}>
                  If an account exists for that email, a reset link is on its way.<br />
                  <span style={{ fontSize: 9, color: TX2 }}>Check your inbox (and spam folder).</span>
                </div>
              ) : (
                <>
                  <Field label="EMAIL" type="email" value={resetEmail} onChange={setResetEmail} placeholder="you@example.com" onEnter={handleForgotPassword} />
                  {error && (
                    <div style={{ background: `${RD}12`, border: `1px solid ${RD}40`, borderRadius: 8, padding: "9px 13px", fontSize: 10, color: RD, marginBottom: 16, lineHeight: 1.5 }}>
                      {error}
                    </div>
                  )}
                  <button
                    onClick={handleForgotPassword}
                    disabled={resetLoading}
                    style={{ width: "100%", background: resetLoading ? CARD2 : `linear-gradient(135deg,${GR},${BL})`, border: "none", borderRadius: 10, padding: "13px 0", color: resetLoading ? TX2 : "#000", fontSize: 11, fontWeight: 800, cursor: resetLoading ? "not-allowed" : "pointer", letterSpacing: 2, fontFamily: "'IBM Plex Mono',monospace", marginBottom: 16, display: "flex", alignItems: "center", justifyContent: "center" }}
                  >
                    {resetLoading ? <Spinner /> : "SEND RESET LINK →"}
                  </button>
                </>
              )}
              <div style={{ textAlign: "center", fontSize: 9, color: TX2 }}>
                <span onClick={() => { setResetMode(false); setResetSent(false); setError(""); }} style={{ color: BL, cursor: "pointer", textDecoration: "underline" }}>← Back to sign in</span>
              </div>
            </>
          ) : (
            <>
              {/* Tab switcher */}
              <div style={{ display: "flex", marginBottom: 24, background: CARD2, borderRadius: 10, padding: 3 }}>
                {[["login","SIGN IN"],["register","REGISTER"]].map(([t, lbl]) => (
                  <button key={t} onClick={() => reset(t)} style={{ flex: 1, background: tab === t ? `linear-gradient(135deg,${GR}18,${BL}18)` : "transparent", border: tab === t ? `1px solid ${BL}40` : "1px solid transparent", borderRadius: 8, padding: "8px 0", color: tab === t ? TX : TX2, fontSize: 9, fontWeight: tab === t ? 700 : 400, letterSpacing: 2, cursor: "pointer", fontFamily: "'IBM Plex Mono',monospace", transition: "all 0.15s" }}>
                    {lbl}
                  </button>
                ))}
              </div>

              {/* Fields */}
              {tab === "register" && <Field label="USERNAME" value={username} onChange={setUsername} placeholder="tradingpro" onEnter={submit} />}
              <Field label="EMAIL" type="email" value={email} onChange={setEmail} placeholder="you@example.com" onEnter={submit} />
              <Field label="PASSWORD" type="password" value={password} onChange={setPassword} placeholder="••••••••" onEnter={submit} />

              {/* Forgot password link (login tab only) */}
              {tab === "login" && (
                <div style={{ textAlign: "right", marginTop: -8, marginBottom: 14 }}>
                  <span
                    onClick={() => { setResetMode(true); setResetEmail(email); setError(""); }}
                    style={{ fontSize: 9, color: TX2, cursor: "pointer", textDecoration: "underline" }}
                  >
                    Forgot password?
                  </span>
                </div>
              )}

              {/* TOS checkbox (register tab only) */}
              {tab === "register" && (
                <div style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 14 }}>
                  <input
                    type="checkbox"
                    id="tos-agree"
                    checked={tosAgreed}
                    onChange={(e) => setTosAgreed(e.target.checked)}
                    style={{ marginTop: 2, accentColor: GR, cursor: "pointer", flexShrink: 0 }}
                  />
                  <label htmlFor="tos-agree" style={{ fontSize: 9, color: TX2, lineHeight: 1.6, cursor: "pointer", fontFamily: "'IBM Plex Mono',monospace" }}>
                    I agree to the{" "}
                    <span
                      onClick={(e) => { e.preventDefault(); setShowTOS(true); }}
                      style={{ color: BL, cursor: "pointer", textDecoration: "underline" }}
                    >
                      Terms of Service
                    </span>
                  </label>
                </div>
              )}

              {/* Error */}
              {error && (
                <div style={{ background: `${RD}12`, border: `1px solid ${RD}40`, borderRadius: 8, padding: "9px 13px", fontSize: 10, color: RD, marginBottom: 16, lineHeight: 1.5 }}>
                  {error}
                </div>
              )}

              {/* Submit */}
              <button
                onClick={submit}
                disabled={loading}
                style={{ width: "100%", background: loading ? CARD2 : `linear-gradient(135deg,${GR},${BL})`, border: "none", borderRadius: 10, padding: "13px 0", color: loading ? TX2 : "#000", fontSize: 11, fontWeight: 800, cursor: loading ? "not-allowed" : "pointer", letterSpacing: 2, fontFamily: "'IBM Plex Mono',monospace", transition: "all 0.15s", marginBottom: 16, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}
              >
                {loading ? <Spinner /> : (tab === "login" ? "SIGN IN →" : "CREATE ACCOUNT →")}
              </button>

              {/* Switch link */}
              <div style={{ textAlign: "center", fontSize: 9, color: TX2 }}>
                {tab === "login" ? (
                  <>No account yet? <span onClick={() => reset("register")} style={{ color: BL, cursor: "pointer", textDecoration: "underline" }}>Register</span></>
                ) : (
                  <>Already have an account? <span onClick={() => reset("login")} style={{ color: BL, cursor: "pointer", textDecoration: "underline" }}>Sign in</span></>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
